import React, { useState } from "react";
import { useAuth } from "../../lib/auth";
import SEO from "../../components/SEO";
import { KeyRound, Save, AlertCircle, ExternalLink, ShieldCheck, Cloud, HardDrive } from "lucide-react";

const DEFAULT_FORM = {
  accountId: "",
  accessKeyId: "",
  secretAccessKey: "",
  bucket: "",
  publicUrl: "",
  endpoint: "",
  password: "",
  password2: "",
};

export default function AdminSetupPage() {
  const { setup } = useAuth();
  const [mode, setMode] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const next = () => setStep((s) => s + 1);
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const steps = mode === "r2"
    ? [
        { title: "راه‌اندازی اولیه", subtitle: "انتخاب حالت ذخیره‌سازی" },
        { title: "اطلاعات Cloudflare R2", subtitle: "توکن با دسترسی Read+Write از Cloudflare R2 بسازید." },
        { title: "رمز عبور ادمین", subtitle: "کلیدهای R2 با این رمز AES-GCM رمزگذاری می‌شوند." },
      ]
    : [
        { title: "راه‌اندازی اولیه", subtitle: "انتخاب حالت ذخیره‌سازی" },
        { title: "رمز عبور ادمین", subtitle: "در حالت محلی، داده‌ها در localStorage مرورگر ذخیره می‌شوند." },
      ];

  const submit = async () => {
    setError("");
    if (form.password.length < 6) {
      setError("رمز عبور باید حداقل ۶ کاراکتر باشد.");
      return;
    }
    if (form.password !== form.password2) {
      setError("دو رمز یکسان نیست.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "r2") {
        const missing = ["accountId", "accessKeyId", "secretAccessKey", "bucket", "publicUrl"].filter(
          (k) => !form[k]?.trim()
        );
        if (missing.length) {
          setError(`فیلدهای الزامی خالی: ${missing.join(", ")}`);
          setBusy(false);
          return;
        }
        await setup({ ...form, mode: "r2" });
      } else {
        await setup({ password: form.password, mode: "local" });
      }
    } catch (e) {
      setError(`خطا: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO title="راه‌اندازی ادمین | تحسین" />
      <section className="min-h-[85vh] px-4 py-14 flex items-center justify-center" data-testid="admin-setup-page">
        <div className="w-full max-w-2xl text-right">
          <span className="text-amber-700 font-semibold text-sm tracking-widest uppercase">INITIAL SETUP</span>
          <h1 className="text-3xl sm:text-4xl font-black text-stone-900 mt-2 mb-3">
            {steps[step]?.title}
          </h1>
          {steps[step]?.subtitle && (
            <p className="text-stone-600 mb-8 leading-relaxed">{steps[step].subtitle}</p>
          )}

          {step > 0 && (
            <div className="flex items-center gap-3 mb-8">
              {steps.map((_, i) => (
                <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-amber-500" : "bg-stone-200"}`} />
              ))}
            </div>
          )}

          <div className="bg-white border border-stone-200 rounded-3xl p-8 shadow-lg shadow-amber-900/5">
            {step === 0 && (
              <div className="space-y-4">
                <p className="text-stone-700 leading-relaxed">
                  سایت تحسین بدون سرور کار می‌کند. انتخاب کنید چطور می‌خواهید شروع کنید:
                </p>

                <button
                  type="button"
                  onClick={() => { setMode("r2"); next(); }}
                  className="w-full text-right bg-amber-50 hover:bg-amber-100 border-2 border-amber-200 hover:border-amber-500 rounded-2xl p-6 transition-all"
                  data-testid="setup-mode-r2"
                >
                  <div className="flex items-start justify-between mb-3">
                    <Cloud className="w-8 h-8 text-amber-600" />
                    <span className="text-xs font-semibold bg-amber-600 text-white px-3 py-1 rounded-full">توصیه‌شده</span>
                  </div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">Cloudflare R2 (production)</h3>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    محصولات، مقالات و تصاویر در باکت R2 ذخیره می‌شوند — از هر مرورگری قابل مدیریت.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => { setMode("local"); next(); }}
                  className="w-full text-right bg-white hover:bg-stone-50 border-2 border-stone-200 hover:border-amber-500 rounded-2xl p-6 transition-all"
                  data-testid="setup-mode-local"
                >
                  <div className="flex items-start justify-between mb-3">
                    <HardDrive className="w-8 h-8 text-stone-600" />
                    <span className="text-xs font-semibold bg-stone-600 text-white px-3 py-1 rounded-full">دِمو/تست</span>
                  </div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">ذخیره‌سازی محلی</h3>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    داده‌ها فقط در همین مرورگر ذخیره می‌شوند. هر وقت خواستید می‌توانید به R2 مهاجرت کنید.
                  </p>
                </button>

                <a
                  href="https://developers.cloudflare.com/r2/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-amber-700 text-sm hover:text-amber-800"
                >
                  <ExternalLink className="w-4 h-4" /> راهنمای Cloudflare R2
                </a>
              </div>
            )}

            {mode === "r2" && step === 1 && (
              <div className="space-y-4">
                <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-xs text-stone-700">
                  <div className="font-bold text-stone-900 mb-2 flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-amber-600" /> چک‌لیست:
                  </div>
                  <ul className="list-disc mr-5 space-y-1">
                    <li>باکت R2 ساخته شده و Public Access فعال شده</li>
                    <li>CORS باکت برای دامنه سایت تنظیم شده (اجازه PUT)</li>
                    <li>توکن API با دسترسی Read & Write گرفته شده</li>
                  </ul>
                </div>
                <Field label="Account ID" value={form.accountId} onChange={(v) => setForm({ ...form, accountId: v })} placeholder="۳۲ کاراکتر hex" testid="setup-account-id" />
                <Field label="Access Key ID" value={form.accessKeyId} onChange={(v) => setForm({ ...form, accessKeyId: v })} testid="setup-access-key" />
                <Field label="Secret Access Key" type="password" value={form.secretAccessKey} onChange={(v) => setForm({ ...form, secretAccessKey: v })} testid="setup-secret-key" />
                <Field label="نام باکت" value={form.bucket} onChange={(v) => setForm({ ...form, bucket: v })} placeholder="tahsin-assets" testid="setup-bucket" />
                <Field label="Public URL" value={form.publicUrl} onChange={(v) => setForm({ ...form, publicUrl: v })} placeholder="https://pub-xxxxxx.r2.dev" testid="setup-public-url" />
                <Field label="Endpoint" optional value={form.endpoint} onChange={(v) => setForm({ ...form, endpoint: v })} placeholder="پیش‌فرض R2 استفاده می‌شود" testid="setup-endpoint" />
              </div>
            )}

            {((mode === "r2" && step === 2) || (mode === "local" && step === 1)) && (
              <div className="space-y-4">
                <Field label="رمز جدید (حداقل ۶ کاراکتر)" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="setup-password" />
                <Field label="تکرار رمز" type="password" value={form.password2} onChange={(v) => setForm({ ...form, password2: v })} testid="setup-password2" />
                <div className="rounded-xl bg-stone-900 text-stone-100 p-4 text-xs flex items-start gap-2">
                  <ShieldCheck className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <span className="leading-relaxed">
                    این رمز قابل بازیابی نیست. اگر فراموش کنید، در حالت R2 باید فایل admin-auth.json را حذف کنید، در حالت محلی باید localStorage پاک شود.
                  </span>
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mt-6" data-testid="admin-setup-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {step > 0 && (
              <div className="flex items-center justify-between gap-3 mt-8 pt-6 border-t border-stone-200">
                <button type="button" onClick={prev} className="px-5 py-2.5 rounded-full border border-stone-200 text-stone-700 hover:border-amber-500" data-testid="setup-prev">
                  قبل
                </button>
                {mode === "r2" && step === 1 ? (
                  <button type="button" onClick={next} className="px-6 py-2.5 rounded-full bg-stone-900 text-white font-semibold hover:bg-stone-800" data-testid="setup-next">
                    مرحله بعد
                  </button>
                ) : (
                  <button type="button" onClick={submit} disabled={busy} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-bold disabled:opacity-60" data-testid="setup-submit">
                    <Save className="w-4 h-4" />
                    {busy ? "در حال راه‌اندازی..." : "پایان راه‌اندازی"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid, optional }) {
  return (
    <div>
      <label className="block text-sm text-stone-700 mb-2 font-medium">
        {label} {optional && <span className="text-amber-600">(اختیاری)</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-200 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 font-mono text-sm"
        data-testid={testid}
      />
    </div>
  );
}
